import { Box, Divider } from "@mui/material";
import { DotLegalDatePicker, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import DotLegalSection from "../../../common/components/dotLegalSection/DotLegalSection.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { calculateAbsoluteDateFromRelativeConfig, getRelativeDateDescription } from "../../../common/relativeDateCalculation.ts";
import { SelectedObligationTemplate } from "../../addContractButton/createContractPane/CreateContractPane.types.ts";
import ObligationTemplateRow from "./obligationTemplateRow/ObligationTemplateRow.tsx";
import { SelectableObligationTemplates } from "../../../entityTemplates/EntityTemplate.types.ts";
import { useObligationTemplatesList } from "./ObligationTemplatesList.hooks.ts";

export interface ObligationTemplatesListProps {
    obligationTemplates: SelectableObligationTemplates[];
    initialSelectedObligations?: SelectedObligationTemplate[];
    contractStartDate?: Date | null;
    contractEndDate?: Date | null;
    onContractStartDateChange: (date: Date | null) => void;
    onContractEndDateChange: (date: Date | null) => void;
    contractEndDateError?: string;
    showErrors: boolean;
    onChange: (obligations: SelectedObligationTemplate[]) => void;
    onValidationChange?: (hasErrors: boolean) => void;
}

function ObligationTemplatesList(props: ObligationTemplatesListProps) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const {
        selectableUsersQuery,
        validationErrors,
        contractStartDateError,
        contractEndDateError,
        toggleSelection,
        updateStartDate,
        updateEndDate,
        updateResponsible,
        getSelectedObligation,
        isSelected,
    } = useObligationTemplatesList({
        obligationTemplates: props.obligationTemplates,
        initialSelectedObligations: props.initialSelectedObligations,
        contractStartDate: props.contractStartDate,
        contractEndDate: props.contractEndDate,
        showErrors: props.showErrors,
        onChange: props.onChange,
        onValidationChange: props.onValidationChange,
    });

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 3 }}>
                <DotLegalHeader headerStyle="small" fontWeight={600} marginBottom={0}>
                    {translateString("contractPeriod")}
                </DotLegalHeader>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, paddingX: 2, marginBottom: 3 }}>
                <DotLegalDatePicker
                    label={translateString("startDate")}
                    value={props.contractStartDate ?? null}
                    onChange={props.onContractStartDateChange}
                    language={getDateTimePickerLanguage()}
                    error={!!(props.showErrors && contractStartDateError)}
                    helperMessage={props.showErrors ? contractStartDateError : undefined}
                    noMargin
                />
                <DotLegalDatePicker
                    label={translateString("endDate")}
                    value={props.contractEndDate ?? null}
                    onChange={props.onContractEndDateChange}
                    language={getDateTimePickerLanguage()}
                    error={!!props.contractEndDateError || !!(props.showErrors && contractEndDateError)}
                    helperMessage={props.contractEndDateError || (props.showErrors ? contractEndDateError : undefined)}
                    noMargin
                />
                <Box />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <DotLegalHeader headerStyle="small" fontWeight={600} toolTip={translateString("selectObligationTemplatesInfo")}>
                    {translateString("selectObligationTemplates")}
                </DotLegalHeader>
            </Box>
            <DotLegalSection>
                {props.obligationTemplates.map((template, index) => {
                    const selected = getSelectedObligation(template.id);
                    const rowIsSelected = isSelected(template.id);
                    const hasRelativeStartDate = template.startDate?.value != null;
                    const hasRelativeEndDate = template.endDate?.value != null;
                    const calculatedStartDate = calculateAbsoluteDateFromRelativeConfig(template.startDate, props.contractStartDate, props.contractEndDate);
                    const calculatedEndDate = calculateAbsoluteDateFromRelativeConfig(template.endDate, props.contractStartDate, props.contractEndDate);
                    const displayStartDate = hasRelativeStartDate ? calculatedStartDate : (selected?.startDate ?? null);
                    const displayEndDate = hasRelativeEndDate ? calculatedEndDate : (selected?.endDate ?? null);
                    const relativeStartDateDescription = hasRelativeStartDate ? getRelativeDateDescription(template.startDate!, translateString) : undefined;
                    const relativeEndDateDescription = hasRelativeEndDate ? getRelativeDateDescription(template.endDate!, translateString) : undefined;
                    const obligationErrors = props.showErrors ? validationErrors.get(template.id) : undefined;
                    const isLastRow = index === props.obligationTemplates.length - 1;

                    return (
                        <Box key={template.id}>
                            <ObligationTemplateRow
                                template={template}
                                selectedObligation={selected}
                                isSelected={rowIsSelected}
                                displayStartDate={displayStartDate}
                                displayEndDate={displayEndDate}
                                relativeStartDateDescription={relativeStartDateDescription}
                                relativeEndDateDescription={relativeEndDateDescription}
                                selectableUsers={selectableUsersQuery.userData?.filter((x) => x.active)}
                                isLoadingUsers={selectableUsersQuery.isLoading}
                                onToggleSelection={() => toggleSelection(template.id)}
                                onStartDateChange={(date) => updateStartDate(template.id, date)}
                                onEndDateChange={(date) => updateEndDate(template.id, date)}
                                onResponsibleChange={(responsibleId) => updateResponsible(template.id, responsibleId)}
                                validationErrors={obligationErrors}
                            />
                            {!isLastRow && <Divider sx={{ marginX: -2, marginY: 2, borderColor: "rgba(0, 0, 0, 0.08)" }} />}
                        </Box>
                    );
                })}
            </DotLegalSection>
        </>
    );
}

export default ObligationTemplatesList;
